import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Chip,
  Collapse
} from '@material-ui/core';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { User, UserHierarchyNode, AuthorizationLevelInfo } from '../../model';

const useStyles = makeStyles((theme) => ({
  nodeContainer: {
    marginBottom: theme.spacing(0.5),
  },
  nodeBox: {
    cursor: 'pointer',
    padding: theme.spacing(0.8, 1),
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.1s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
  },
  selectedNode: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  nodeContent: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    marginRight: theme.spacing(1.2),
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: '0.8rem',
  },
  userInfo: {
    flex: 1,
    minWidth: 0, // Allow text to truncate
  },
  userName: {
    fontSize: '0.85rem',
    fontWeight: 500,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expandButton: {
    marginLeft: theme.spacing(0.5),
    padding: theme.spacing(0.3),
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  },
  childrenContainer: {
    marginLeft: theme.spacing(2.5),
    paddingLeft: theme.spacing(1),
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  positionChip: {
    height: 16,
    fontSize: '0.6rem',
    marginTop: theme.spacing(0.3),
    marginLeft: theme.spacing(0.5),
  },
}));

interface UserHierarchyNodeComponentProps {
  node: UserHierarchyNode;
  level: number;
  isExpanded: boolean;
  isSelected: boolean;
  expandedNodes: Set<number>;
  selectedUserId?: number;
  authorizationLevels: AuthorizationLevelInfo[];
  allUsers: User[];
  onUserSelect: (user: User) => void;
  onNodeToggle: (userId: number) => void;
  onUpdateReporting: (userId: number, newManagerId: number | null) => void;
  getAuthorizationLevelColor: (level: number) => string;
}

const UserHierarchyNodeComponent: React.FC<UserHierarchyNodeComponentProps> = ({
  node,
  level,
  isExpanded,
  isSelected,
  expandedNodes,
  selectedUserId,
  authorizationLevels,
  allUsers,
  onUserSelect,
  onNodeToggle,
  onUpdateReporting,
  getAuthorizationLevelColor,
}) => {
  const classes = useStyles();
  const hasChildren = node.children && node.children.length > 0;

  const handleNodeClick = () => {
    onUserSelect(node.user);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onNodeToggle(node.user.id);
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getAuthorizationLevelInfo = (user: User): AuthorizationLevelInfo | undefined => {
    if (!user.authorizationLevel) return undefined;
    
    // Try to find by numeric value first
    const numericValue = Number(user.authorizationLevel);
    if (!isNaN(numericValue)) {
      const levelByValue = authorizationLevels.find(l => l.value === numericValue);
      if (levelByValue) return levelByValue;
    }
    
    // Try to find by string name (for enum values like "STAFF")
    const levelByName = authorizationLevels.find(l => 
      l.name.toUpperCase() === String(user.authorizationLevel).toUpperCase()
    );
    if (levelByName) return levelByName;
    
    return undefined;
  };

  return (
    <Box className={classes.nodeContainer}>
      <Box 
        className={`${classes.nodeBox} ${isSelected ? classes.selectedNode : ''}`}
        onClick={handleNodeClick}
      >
        <Box className={classes.nodeContent}>
          <Avatar className={classes.avatar}>
            {getInitials(node.user.name)}
          </Avatar>
          
          <Box className={classes.userInfo}>
            <Typography className={classes.userName} component="div">
              {node.user.name}
            </Typography>
            
            {getAuthorizationLevelInfo(node.user) && (
              <Chip
                size="small"
                label={getAuthorizationLevelInfo(node.user)!.name}
                className={classes.positionChip}
                style={{
                  backgroundColor: getAuthorizationLevelColor(getAuthorizationLevelInfo(node.user)!.value),
                  color: 'white',
                }}
              />
            )}
          </Box>

          {hasChildren && (
            <IconButton
              className={classes.expandButton}
              size="small"
              onClick={handleExpandClick}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      </Box>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box className={classes.childrenContainer}>
            {node.children.map((childNode) => (
              <UserHierarchyNodeComponent
                key={childNode.user.id}
                node={childNode}
                level={level + 1}
                isExpanded={expandedNodes.has(childNode.user.id)}
                isSelected={selectedUserId === childNode.user.id}
                expandedNodes={expandedNodes}
                selectedUserId={selectedUserId}
                authorizationLevels={authorizationLevels}
                allUsers={allUsers}
                onUserSelect={onUserSelect}
                onNodeToggle={onNodeToggle}
                onUpdateReporting={onUpdateReporting}
                getAuthorizationLevelColor={getAuthorizationLevelColor}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default UserHierarchyNodeComponent;